/**
 * The PulseAssist Email mark, inline.
 *
 * ═══ Why inline and not <img src="/brand/icon.svg"> ═══════════════════════════
 *
 * It was an `<img>` pointing at `public/brand/icon.svg`, and that shipped a logo nobody could see.
 *
 * Vite fingerprints everything under `src/` — `assets/index-<hash>.js` — and serve.mjs caches those for
 * a year as `immutable`, which is correct and safe. Files in `public/` are copied VERBATIM: same path,
 * no hash. So `/brand/icon.svg` gets `cache-control: public, max-age=3600`, and after a deploy a browser
 * keeps serving the PREVIOUS file from disk for up to an hour.
 *
 * The result was the worst kind of half-update: the JavaScript was new, so the rail rendered the new
 * `<img>` element, while the bytes behind it were the OLD envelope — a logo that had been replaced,
 * displayed by code that had already shipped. Verified from production: the server returned the new
 * monogram on request while the browser showed the old one.
 *
 * Inlining removes the class of problem rather than waiting it out. The mark becomes part of the hashed
 * bundle, so it is versioned with the code that uses it, cached for a year like the rest of it, and
 * cannot be a deploy behind. It also removes a network request from first paint, which is the thing this
 * console was being tuned for.
 *
 * `public/brand/icon.svg` stays as the FAVICON — index.html has to name a real URL, and a browser tab
 * icon being an hour stale is a cosmetic lag, not a wrong logo in the product chrome.
 *
 * ═══ The artwork ═════════════════════════════════════════════════════════════
 *
 * Identical geometry to the PulseAssist monogram in support-flow — the same "PA" slab construction, the
 * same coordinates, the same forward lean. It differs only in colour: the house gradient runs
 * cyan → blue → violet, PulseAssist AI takes the violet end and Email takes the BLUE end, which is this
 * console's accent. Siblings, not strangers, and the "which tab am I in" question is answered by hue
 * instead of by drawing a different object.
 *
 * Gradient ids are namespaced (`pae-*`) because both marks can appear in one document — the product
 * switcher lists the AI product — and duplicate SVG ids silently resolve to the first one, which would
 * paint this mark in the AI palette.
 */

export function BrandMark({ size = 22 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className="block shrink-0"
    >
      <defs>
        <linearGradient id="pae-mark" x1="18%" y1="4%" x2="72%" y2="100%">
          <stop offset="0%" stopColor="#5B9BFF" />
          <stop offset="34%" stopColor="#2563EB" />
          <stop offset="72%" stopColor="#1B4CD8" />
          <stop offset="100%" stopColor="#12329B" />
        </linearGradient>
        <linearGradient id="pae-mark-deep" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1D4ED8" />
          <stop offset="55%" stopColor="#12329B" />
          <stop offset="100%" stopColor="#0B2273" />
        </linearGradient>
        <linearGradient id="pae-mark-edge" x1="0%" y1="0%" x2="100%" y2="60%">
          <stop offset="0%" stopColor="#A5C8FF" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#5B9BFF" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Bowl of the "P": angular chevron, leaning forward. */}
      <path
        d="M 66 30 L 140 30 L 168 58 L 168 84 L 140 112 L 96 112 L 96 86 L 132 86 L 140 78 L 140 64 L 132 56 L 66 56 Z"
        fill="url(#pae-mark)"
      />
      {/* Stem of the "P" / spine of the "A". */}
      <path d="M 66 30 L 96 56 L 96 170 L 66 170 Z" fill="url(#pae-mark)" />
      {/* The "A" crossbar, sheared to carry the lean. */}
      <path d="M 62 92 L 92 118 L 46 170 L 16 170 Z" fill="url(#pae-mark-deep)" />
      {/* Leg of the "A", balancing the mark bottom-right. */}
      <path d="M 118 132 L 150 132 L 150 170 Z" fill="url(#pae-mark-deep)" />
      {/* Bright cut along the top-left, so the slabs read as layered. */}
      <path d="M 66 30 L 140 30 L 146 36 L 72 36 Z" fill="url(#pae-mark-edge)" />
    </svg>
  );
}
